import { useState } from 'react';
import PerfectScrollbar from 'react-perfect-scrollbar';
import PropTypes from 'prop-types';
import {
  Box,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TableContainer,
  IconButton,
  Chip,
  Link,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';

import ItemListToolbar from './list-toolbar';
import Iconify from '../Iconify';

import app from 'firebase/compat/app';
import 'firebase/compat/firestore';
import 'firebase/compat/storage';
import { debugL } from '../../custom';

const storage = app.storage();

function applyFilter(array, fields, query) {
  if (query) {
    return array.filter((_item) => {
      for (let field of fields) {
        if (_item[field] && _item[field].toLowerCase().indexOf(query.toLowerCase()) !== -1) {
          return true;
        }
      }
      return false;
    });
  } else {
    return array;
  }
}

function loberzStatusColor(loberzStatus) {
  if (['paid', 'picked', 'wait_ts', 'canceled'].includes(loberzStatus)) {
    return 'info';
  } else if (['to_collect'].includes(loberzStatus)) {
    return 'error';
  } else if (['wait_min_ts', 'err_dest', 'canceled_late'].includes(loberzStatus)) {
    return 'warning';
  } else if (['locked', 'imminent'].includes(loberzStatus)) {
    return 'warning';
  } else if (['delivered'].includes(loberzStatus)) {
    return 'success';
  }
}

const loberzStatuses = {
  paid: 'Prête',
  picked: 'Collectée',
  wait_ts: 'RDV à venir',
  canceled: 'Annulée',
  to_collect: 'Collecte Magasin',
  wait_min_ts: 'En attente',
  locked: 'Engagée',
  imminent: 'Imminente',
  delivered: 'Livrée',
  canceled_late: 'Err. Exp.',
  err_dest: 'Err. Dest.',
};

export const ItemListResults = ({ items, attributes, bulkActions, itemActions, isProcessing, filterFields, selectable, ...rest }) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  debugL > 1 && console.log('isProcessing', isProcessing);

  const [selectedItemIds, setSelectedItemIds] = useState([]);
  const [limit, setLimit] = useState(50);
  const [page, setPage] = useState(0);
  const [filterName, setFilterName] = useState('');

  const [anchorEl, setAnchorEl] = useState(null);
  const [currentItemId, setCurrentItemId] = useState(null);
  const open = Boolean(anchorEl);

  // --- MODIFICATION: List of field names for columns that should NOT expand ---
  // Note: Adjust these field names to match exactly those in your `attributes` object
  const narrowColumnFields = ['reference', 'units', 'opt', 'code', 'loberzStatus', 'stockState'];


  const openInNewTab = (url) => {
    const newWindow = window.open(url, '_blank', 'noopener,noreferrer');
    if (newWindow) newWindow.opener = null;
  };

  const openDeliveryNote = async (path) => {
    const ref = storage.ref(path);
    let url = '';
    try {
      url = await ref.getDownloadURL();
      openInNewTab(url);
    } catch (e) {
      console.log('error while getting download url');
      console.log(e.message);
    }
  };

  const handleMenuOpen = (event, itemId) => {
    setAnchorEl(event.currentTarget);
    setCurrentItemId(itemId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setCurrentItemId(null);
  };

  const handleMenuItemClick = (handler) => {
    if (currentItemId) {
      handler(currentItemId);
    }
    handleMenuClose();
  };

  const handleFilterByName = (event) => {
    setFilterName(event.target.value);
  };

  const handleSelectAll = (event) => {
    let newSelectedItemIds;
    if (event.target.checked) {
      newSelectedItemIds = items.map((item) => item.id);
    } else {
      newSelectedItemIds = [];
    }
    setSelectedItemIds(newSelectedItemIds);
  };

  const emptySelection = () => setSelectedItemIds([]);

  const handleSelectOne = (event, id) => {
    const selectedIndex = selectedItemIds.indexOf(id);
    let newSelectedItemIds = [];

    if (selectedIndex === -1) {
      newSelectedItemIds = newSelectedItemIds.concat(selectedItemIds, id);
    } else if (selectedIndex === 0) {
      newSelectedItemIds = newSelectedItemIds.concat(selectedItemIds.slice(1));
    } else if (selectedIndex === selectedItemIds.length - 1) {
      newSelectedItemIds = newSelectedItemIds.concat(selectedItemIds.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelectedItemIds = newSelectedItemIds.concat(
        selectedItemIds.slice(0, selectedIndex),
        selectedItemIds.slice(selectedIndex + 1)
      );
    }
    setSelectedItemIds(newSelectedItemIds);
  };

  const handleLimitChange = (event) => {
    setPage(0);
    setLimit(event.target.value);
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const filteredItems = applyFilter(items, filterFields, filterName);

  return (
    <>
      <Box>
        <ItemListToolbar
          bulkActions={bulkActions}
          selectedItemIds={selectedItemIds}
          emptySelection={emptySelection}
          filterName={filterName}
          onFilterName={handleFilterByName}
        />
        <TableContainer component="div">
          <PerfectScrollbar>
            {/* MODIFICATION: Removed width: 'auto' to let the table fill the space */}
            <Table sx={{ minWidth: isMobile ? 100 : 700 }}>
              <TableHead>
                <TableRow>
                  {/* <TableCell padding="checkbox" sx={{ width: '1%' }} /> */}
                  {selectable ?
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedItemIds.length === items.length && items.length > 0}
                        color="primary"
                        indeterminate={
                          selectedItemIds.length > 0
                          && selectedItemIds.length < items.length
                        }
                        onChange={handleSelectAll}
                      />
                    </TableCell> : null
                  }
                  {Object.entries(attributes).map(([col, field]) => {
                    // MODIFICATION: Apply style only to narrow columns
                    const style = narrowColumnFields.includes(field)
                      ? { width: '1%', whiteSpace: 'nowrap' }
                      : {};
                    return (<TableCell key={col} sx={style}>{col}</TableCell>);
                  })}
                  {/* MODIFICATION: Set a small, fixed width for the action column header */}
                  {itemActions ? <TableCell padding="none" sx={{ width: '60px' }} /> : null}
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredItems.slice(page * limit, page * limit + limit).map((item) => (
                  <TableRow hover key={item.id} selected={selectedItemIds.indexOf(item.id) !== -1}>
                    {
                      selectable ?
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedItemIds.indexOf(item.id) !== -1}
                          onChange={(event) => handleSelectOne(event, item.id)}
                          value="true"
                          disabled={item.quantity != null && item.quantity != 0}
                        />
                      </TableCell> : null
                    }
                    {Object.entries(attributes).map(([col, field]) => {
                      // MODIFICATION: Apply style only to narrow columns
                      const style = narrowColumnFields.includes(field)
                        ? { width: '1%', whiteSpace: 'nowrap' }
                        : {};

                      if (field === 'loberzStatus') {
                        return (
                          <TableCell key={col} sx={style}>
                            <Chip label={loberzStatuses[item[field]]} color={loberzStatusColor(item[field])} />
                          </TableCell>
                        );
                      } else if (field === 'stockState') {
                        const quantity = item.quantity;
                        const threshold = item.lowThreshold;
                        var label = 'Normal';
                        var status = 'delivered';
                        if (quantity == 0) {
                          label = 'Epuisé';
                          status = 'to_collect';
                        } else if (quantity <= threshold) {
                          label = 'Bas';
                          status = 'imminent';
                        }
                        return (
                          <TableCell key={col} sx={style}>
                            <Chip label={label} color={loberzStatusColor(status)} />
                          </TableCell>
                        );
                      } else if (field === 'deliveryNotePath') {
                        const deliveryNotePath = item['deliveryNotePath'];
                        const hasDeliveryNote = deliveryNotePath && deliveryNotePath != 'MISSING_PATH' ? 'OUI' : 'NON';
                        return (
                          <TableCell key={col} sx={style}>
                            {hasDeliveryNote === 'OUI' ? (
                              <Link component="button" onClick={() => openDeliveryNote(deliveryNotePath)}>OUI</Link>
                            ) : (
                              'Non'
                            )}
                          </TableCell>
                        );
                      } else {
                        return <TableCell key={col} sx={style}>{item[field]}</TableCell>;
                      }
                    })}
                    {/* MODIFICATION: Set a small, fixed width for the action cell */}
                    <TableCell padding="none" align="right" sx={{ width: '60px' }}>
                      {itemActions && itemActions.length > 0 && (
                        <IconButton onClick={(event) => handleMenuOpen(event, item.id)} disabled={isProcessing}>
                          <Iconify icon="eva:more-vertical-fill" />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </PerfectScrollbar>
        </TableContainer>
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleMenuClose}
        MenuListProps={{ 'aria-labelledby': 'basic-button' }}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {/* {itemActions?.map((action) => (
          <MenuItem key={action.title} onClick={() => handleMenuItemClick(action.handler)} disabled={isProcessing}>
            <ListItemIcon>
              <Iconify icon={action.iconName} width={22} height={22} />
            </ListItemIcon>
            <ListItemText>{action.title}</ListItemText>
          </MenuItem>
        ))} */}
        {(() => {
          const currentItem = items.find(i => i.id === currentItemId);
          if (!currentItem) return null;

          return itemActions?.
          	filter(action => action.isVisible ? action.isVisible(currentItem.data) : true)
          	.map((action) => (
            	<MenuItem key={action.title} onClick={() => handleMenuItemClick(action.handler)} disabled={isProcessing}>
            	  <ListItemIcon>
            	    <Iconify icon={action.iconName} width={22} height={22} />
            	  </ListItemIcon>
            	  <ListItemText>{action.title}</ListItemText>
          	</MenuItem>
        	));
        })()}
      </Menu>

      <TablePagination
        component="div"
        count={filteredItems.length}
        labelRowsPerPage="Lignes par page :"
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleLimitChange}
        page={page}
        rowsPerPage={limit}
        rowsPerPageOptions={[25, 50, 100, 200]}
      />
    </>
  );
};

ItemListResults.propTypes = {
  items: PropTypes.array.isRequired,
  attributes: PropTypes.object,
  bulkActions: PropTypes.array,
  itemActions: PropTypes.array,
  isProcessing: PropTypes.bool,
  filterFields: PropTypes.array,
  selectable: PropTypes.bool,
};